import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Camera, RotateCcw, RotateCw, Trash2, Loader2, Image as ImageIcon } from 'lucide-react';
import API_URL from '../api';
import ConfirmModal from './ConfirmModal';
import './DishManagement.css';
import { useToast } from '../contexts/ToastContext';

import { isAssetUrl, resolveAssetUrl } from '../utils/assetUrl';
import { getUploadErrorMessage } from '../utils/uploadError';
import {
  DEFAULT_EMOJI_DISH_SIZE,
  DEFAULT_PHOTO_DISH_SIZE,
  getDefaultDishDimensions,
} from './virtualDesk/dishSizing';

const PRESET_ICONS = [
  '🍽️', '🍷', '🍴', '🔪', '🥄', '☕', '🍲', '🥣',
  '🥡', '🥢', '🍱', '🍻', '🥃', '🍞', '🥤', '🍜',
  '🥗', '🍕', '🍔', '🍟', '🍳', '🥓', '🥪', '🌮',
  '🍣', '🍤', '🍦', '🍰', '🧁', '🍩', '🍪', '🍎',
  '🍇', '🍓', '🍋', '🥑', '🥦', '🥩', '🍗', '🥨',
  '🥐', '🥯', '🥞', '🍙', '🍢', '🍡', '🍮', '🍯',
  '🍹', '🍸', '🧉', '🍾', '🍵', '🍶', '🧂', '🧂',
];


const DEFAULT_ICON = '🍽️';
const normalizeRotation = (value) => ((value % 360) + 360) % 360;
const MIN_DISH_SIZE = 28;
const MAX_DISH_SIZE = 480;

const DishManagement = () => {
  const [dishes, setDishes] = useState([]);
  const [editingDish, setEditingDish] = useState(null);
  const [name, setName] = useState('');
  const [icon, setIcon] = useState(DEFAULT_ICON);
  const [rotation, setRotation] = useState(0);
  const [width, setWidth] = useState(DEFAULT_EMOJI_DISH_SIZE);
  const [height, setHeight] = useState(DEFAULT_EMOJI_DISH_SIZE);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, idToDelete: null });
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const toast = useToast();

  const fetchDishes = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/dishes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDishes(res.data);
    } catch (err) {
      console.error(err);
      toast.error('Помилка завантаження посуду');
    }
  }, [toast]);

  useEffect(() => {
    fetchDishes();
  }, [fetchDishes]);

  const resetForm = () => {
    setEditingDish(null);
    setName('');
    setIcon(DEFAULT_ICON);
    setRotation(0);
    setWidth(DEFAULT_EMOJI_DISH_SIZE);
    setHeight(DEFAULT_EMOJI_DISH_SIZE);
  };

  const handleRotate = (delta) => {
    setRotation((current) => normalizeRotation(current + delta));
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!name.trim()) {
      toast.error('Введіть назву предмету');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const payload = {
        name: name.trim(),
        icon,
        rotation: normalizeRotation(rotation),
        width: Math.max(MIN_DISH_SIZE, Math.min(MAX_DISH_SIZE, Number(width) || DEFAULT_EMOJI_DISH_SIZE)),
        height: Math.max(MIN_DISH_SIZE, Math.min(MAX_DISH_SIZE, Number(height) || DEFAULT_EMOJI_DISH_SIZE)),
      };
      const config = { headers: { Authorization: `Bearer ${token}` } };

      if (editingDish) {
        await axios.put(`${API_URL}/dishes/${editingDish._id}`, payload, config);
        toast.success('Предмет оновлено успішно');
      } else {
        await axios.post(`${API_URL}/dishes`, payload, config);
        toast.success('Предмет додано успішно');
      }

      resetForm();
      fetchDishes();
    } catch (err) {
      console.error(err);
      toast.error('Сталася помилка при збереженні');
    }
  };

  const handleConfirmDelete = async () => {
    const id = confirmModal.idToDelete;
    if (!id) return;

    setConfirmModal({ isOpen: false, idToDelete: null });

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/dishes/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Предмет успішно видалено');
      fetchDishes();
    } catch (err) {
      console.error(err);
      toast.error('Сталася помилка при видаленні');
    }
  };

  const startEdit = (dish) => {
    const dimensions = getDefaultDishDimensions(dish);
    setEditingDish(dish);
    setName(dish.name);
    setIcon(dish.icon || DEFAULT_ICON);
    setRotation(normalizeRotation(dish.rotation || 0));
    setWidth(dimensions.width);
    setHeight(dimensions.height);
  };

  const deleteClick = (id) => {
    setConfirmModal({ isOpen: true, idToDelete: id });
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      setUploading(true);
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      setIcon(res.data.url);
      setRotation(0);
      setWidth((current) => (
        current === DEFAULT_EMOJI_DISH_SIZE ? DEFAULT_PHOTO_DISH_SIZE : current
      ));
      setHeight((current) => (
        current === DEFAULT_EMOJI_DISH_SIZE ? DEFAULT_PHOTO_DISH_SIZE : current
      ));
      toast.success('Іконку завантажено');
    } catch (err) {
      console.error(err);
      toast.error(getUploadErrorMessage(err, 'файлу'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const renderIcon = (iconValue, options = {}) => {
    if (!iconValue) return null;

    const {
      className = '',
      rotationValue = 0,
    } = options;
    const normalizedRotation = normalizeRotation(rotationValue);

    if (isAssetUrl(iconValue)) {
      return (
        <img
          src={resolveAssetUrl(iconValue)}
          alt="icon"
          className={className}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            transform: `rotate(${normalizedRotation}deg)`,
          }}
        />
      );
    }

    return (
      <span className={className} style={{ transform: `rotate(${normalizedRotation}deg)` }}>
        {iconValue}
      </span>
    );
  };

  const isPhotoIcon = isAssetUrl(icon);

  const maxPreviewSize = 160;
  const w = Number(width) || DEFAULT_EMOJI_DISH_SIZE;
  const h = Number(height) || DEFAULT_EMOJI_DISH_SIZE;
  const ratio = Math.min(maxPreviewSize / w, maxPreviewSize / h, 1);
  const previewWidth = w * ratio;
  const previewHeight = h * ratio;

  return (
    <div className="dish-management-container">
      <header className="content-header">
        <h2>🍽️ Керування предметами столу</h2>
      </header>

      <div className="dish-layout">
        <div className="dish-form-card">
          <h3>{editingDish ? 'Редагувати предмет' : 'Додати новий предмет'}</h3>
          <form onSubmit={handleSave}>
            <div className="form-group">
              <label>Назва предмету</label>
              <input
                type="text"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Напр. Бокал для вина"
              />
            </div>

            <div className="form-group">
              <label>Іконка (Емодзі або фото)</label>
              <div className="icon-selector-container">
                <div className="icon-section-label">Виберіть емодзі</div>
                <div className="icon-presets-grid">
                  {PRESET_ICONS.map((presetIcon) => (
                    <button
                      type="button"
                      key={presetIcon}
                      className={`icon-btn ${icon === presetIcon ? 'active' : ''}`}
                      onClick={() => {
                        setIcon(presetIcon);
                        setRotation(0);
                        if (!editingDish) {
                          setWidth(DEFAULT_EMOJI_DISH_SIZE);
                          setHeight(DEFAULT_EMOJI_DISH_SIZE);
                        }
                      }}
                    >
                      {presetIcon}
                    </button>
                  ))}

                  <div className="custom-emoji-wrapper">
                    <input
                      type="text"
                      value={isPhotoIcon ? '' : icon}
                      onChange={(event) => {
                        setIcon(event.target.value);
                        setRotation(0);
                      }}
                      placeholder="Своє 💎"
                      className="custom-icon-input"
                      maxLength={4}
                      disabled={isPhotoIcon}
                    />
                  </div>
                </div>

                <div className="icon-section-label">Або завантажте власне фото</div>
                <div className="icon-upload-actions">

                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                    accept="image/*"
                  />

                  <button
                    type="button"
                    className={`btn-upload-large ${isPhotoIcon ? 'has-photo' : ''}`}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                  >
                    {uploading ? (
                      <Loader2 className="animate-spin" size={20} />
                    ) : isPhotoIcon ? (
                      <ImageIcon size={20} />
                    ) : (
                      <Camera size={20} />
                    )}
                    <span>{uploading ? 'Завантаження...' : isPhotoIcon ? 'Змінити фото' : 'Завантажити фото'}</span>
                  </button>

                  {isPhotoIcon && (
                    <div className="photo-edit-group">
                      <button
                        type="button"
                        className="btn-edit-icon"
                        onClick={() => handleRotate(-90)}
                        title="Повернути вліво"
                      >
                        <RotateCcw size={18} />
                      </button>
                      <button
                        type="button"
                        className="btn-edit-icon"
                        onClick={() => handleRotate(90)}
                        title="Повернути вправо"
                      >
                        <RotateCw size={18} />
                      </button>
                      <button
                        type="button"
                        className="btn-edit-icon btn-danger-action"
                        onClick={() => {
                          setIcon(DEFAULT_ICON);
                          setRotation(0);
                          setWidth(DEFAULT_EMOJI_DISH_SIZE);
                          setHeight(DEFAULT_EMOJI_DISH_SIZE);
                        }}
                        title="Видалити фото"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {isPhotoIcon && (
                <div className="icon-preview-tools">
                  <span className="rotation-indicator">Поворот: {normalizeRotation(rotation)}°</span>
                </div>
              )}

              <div className="icon-preview-large">
                <div 
                  className="icon-preview-stage"
                  style={{
                    width: `${previewWidth}px`,
                    height: `${previewHeight}px`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: `${Math.min(previewWidth, previewHeight) * 0.65}px`,
                    lineHeight: 1
                  }}
                >
                  {renderIcon(icon, { rotationValue: rotation })}
                </div>
              </div>
            </div>


            <div className="form-group">
              <label>Стартовий розмір на столі</label>
              <div className="dish-size-grid">
                <div>
                  <span className="dish-size-label">Ширина</span>
                  <input
                    type="number"
                    min={MIN_DISH_SIZE}
                    max={MAX_DISH_SIZE}
                    step="2"
                    value={width}
                    onChange={(event) => setWidth(event.target.value)}
                  />
                </div>
                <div>
                  <span className="dish-size-label">Висота</span>
                  <input
                    type="number"
                    min={MIN_DISH_SIZE}
                    max={MAX_DISH_SIZE}
                    step="2"
                    value={height}
                    onChange={(event) => setHeight(event.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary">
                {editingDish ? 'Зберегти зміни' : 'Створити'}
              </button>
              {editingDish && (
                <button type="button" className="btn-secondary" onClick={resetForm}>
                  Скасувати
                </button>
              )}
            </div>
          </form>
        </div>

        <div className="dish-list-card">
          <h3>Збережені предмети ({dishes.length})</h3>
          {dishes.length === 0 ? (
            <div className="empty-dish-msg">
              У вас ще немає доданих предметів. Створіть перший зліва!
            </div>
          ) : (
            <div className="dish-list-scroll">
              <div className="dish-grid">
                {dishes.map((dish) => {
                  const dimensions = getDefaultDishDimensions(dish);
                  const maxListSize = 84;
                  const listRatio = Math.min(maxListSize / dimensions.width, maxListSize / dimensions.height, 1);
                  const listWidth = dimensions.width * listRatio;
                  const listHeight = dimensions.height * listRatio;

                  return (
                    <div key={dish._id} className="dish-item-card">
                      <div 
                        className="dish-icon-preview"
                        style={{
                          width: `${listWidth}px`,
                          height: `${listHeight}px`,
                          fontSize: `${Math.min(listWidth, listHeight) * 0.6}px`,
                          lineHeight: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {renderIcon(dish.icon, { rotationValue: dish.rotation || 0 })}
                      </div>
                    <div className="dish-details">
                      <h4>{dish.name}</h4>
                      <span>Розмір: {getDefaultDishDimensions(dish).width} x {getDefaultDishDimensions(dish).height}px</span>
                      <span>Поворот: {normalizeRotation(dish.rotation || 0)}°</span>
                      <span>Додано: {new Date(dish.createdAt).toLocaleDateString()}</span>
                    </div>
                    <div className="dish-actions">
                      <button className="btn-icon" onClick={() => startEdit(dish)} title="Редагувати">✏️</button>
                      <button className="btn-icon btn-danger" onClick={() => deleteClick(dish._id)} title="Видалити">🗑️</button>
                    </div>
                  </div>
                );
              })}
              </div>
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title="Видалення предмету"
        message="Ви впевнені, що хочете видалити цей предмет столу? Це може вплинути на існуючі сервіровки."
        confirmText="Видалити предмет"
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmModal({ isOpen: false, idToDelete: null })}
      />
    </div>
  );
};

export default DishManagement;
